//interface
import { SplitPayMeta, SplitPayData, SPLITPAY_POWERED_BY } from "@payment-ui/interfaces/SplitPay";

//helpers
import { getIconCloudinaryUrl as getCloudinaryUrl } from "@payment-ui/helpers/Icon";
import { isEmpty } from "@swiggy-private/js-utils";

// types
import { VALID_PAYMENT_METHODS } from "@swiggy-private/payment-types";

// constants
import { TWID_PAY_ICON } from "@payment-ui/constants";

export const getSplitPayIconUrl = (metadata: SplitPayMeta, disabled = false): string =>
    getCloudinaryUrl(metadata.iconUrl, { grayscale: disabled });

export const getSplitPayDisplayName = (data: SplitPayData): string => data.displayName;

export const isSplitPayMethodEnabled = (data: SplitPayData): boolean => data.enabled;

export const getSplitPayBalance = (data: SplitPayData): string => data.eligibleBalance;

export const getSplitpayHelperMeta = (data: SplitPayData) => {
    return {
        isEnabled: isSplitPayMethodEnabled(data),
        showPromoMessage: isSplitPayMethodEnabled(data) && !isEmpty(data.meta?.promoMsg),
        showOfferMessage: isSplitPayMethodEnabled(data) && !isEmpty(data.meta?.offerMessage),
        showBannerMessage: isSplitPayMethodEnabled(data) && !isEmpty(data.meta?.bannerMessage),
        showDisableMessage: !isSplitPayMethodEnabled(data) && !isEmpty(data.meta?.disableMessage),
        poweredByLogo: getSplitpayPoweredByLogo(data),
    };
};

export const getSplitpayIdentifier = (data: SplitPayData) => {
    if (!isEmpty(data.splitPayIdentifier)) {
        return data.splitPayIdentifier;
    } else if (!isEmpty(data.paymentCode)) {
        return data.paymentCode;
    }
    return "";
};

export const getSplitpayPoweredByLogo = (data: SplitPayData): string => {
    if (data.poweredBy === SPLITPAY_POWERED_BY.TWID) {
        return TWID_PAY_ICON;
    }
    return "";
};

export const isSplitPayMethodSwiggyPay = (method: SplitPayData): boolean =>
    method.paymentCode?.toUpperCase() === VALID_PAYMENT_METHODS.SWIGGYPAY;

export const isSplitPayMethodRewardAndSelected = (method: SplitPayData): boolean => method.isReward && method.select;

export const isSplitPayMethodReward = (method: SplitPayData): boolean => method.isReward;

export const isSplitPayMethodMMT = (method: SplitPayData): boolean =>
    method.paymentCode?.toUpperCase() === VALID_PAYMENT_METHODS.MMT;

export const showViewAllRewards = (splitpayList: SplitPayData[]) =>
    splitpayList.filter(isSplitPayMethodReward).length > 1;

/**
 * 1 - remove rewards if it is friction layout
 *
 * 2 - If there is SwiggyPay method in split pay list and the eligibleBalance is 0,
 * we need to remove it from list. This check is ONLY needed for SwiggyPay due to legacy backend code.
 *
 * 3 - filter based on if a coupon or reward is applied
 */
export const getUpdatedSplitPayList = (
    splitPayList: SplitPayData[],
    couponApplied?: boolean,
    rewardApplied?: boolean,
    isFrictionLayout?: boolean,
) => {
    let eligibleSplitpayList = [...splitPayList];

    if (isFrictionLayout) {
        eligibleSplitpayList = eligibleSplitpayList.filter((method) => !method.isReward);
    }

    eligibleSplitpayList = eligibleSplitpayList?.filter(
        (method) => !(isSplitPayMethodSwiggyPay(method) && Number(method?.eligibleBalance) === 0),
    );

    if (couponApplied) {
        eligibleSplitpayList = eligibleSplitpayList.filter((method) => method.couponApplicable);
    }

    if (rewardApplied) {
        eligibleSplitpayList = eligibleSplitpayList.filter((method) => method.rewardApplicable);
    }

    return eligibleSplitpayList;
};

export const getRewardsSplitpayList = (
    splitpayList: SplitPayData[],
    couponApplied?: boolean,
    rewardApplied?: boolean,
) => {
    return getUpdatedSplitPayList(splitpayList, couponApplied, rewardApplied).filter(isSplitPayMethodReward);
};

/**
 *
 * @param splitPayList
 * returns [enabledSplitMethods, disabledSplitpayMethods]
 */
export const getSplitpayRewardsByStatus = (splitPayList: SplitPayData[]) => {
    const enabledSplitPayList: SplitPayData[] = [];
    const enabledWithUpiUnavailable: SplitPayData[] = [];
    const disabledSplitPayList: SplitPayData[] = [];

    splitPayList.forEach((item) => {
        if (!item.enabled) {
            disabledSplitPayList.push(item);
            return;
        }

        if (item.upiAppUnavailable) {
            enabledWithUpiUnavailable.push(item);
            return;
        }

        enabledSplitPayList.push(item);
    });

    return [[...enabledSplitPayList, ...enabledWithUpiUnavailable], disabledSplitPayList];
};

export const getFirstRewardFromList = (splitPayList: SplitPayData[]) => {
    return splitPayList.find((method) => method.isReward);
};

/**
 *
 * @param splitpayList
 * @returns {
 *      firstFoldSplitPayList: array of the splitpay methods to be shown in first fold and whether there are
 *      canShowViewAllRewards: can we show view all rewards cta or not
 * }
 */
export const getFoldsInfoForSplitpayMethods = (
    splitpayList: SplitPayData[],
    isFrictionLayout = false,
): { firstFoldSplitPayList: SplitPayData[]; canShowViewAllRewards: boolean } => {
    if (isFrictionLayout) {
        return { firstFoldSplitPayList: splitpayList, canShowViewAllRewards: false };
    }

    const smPay = splitpayList.find(isSplitPayMethodSwiggyPay);
    let rewardPay = splitpayList.find(isSplitPayMethodRewardAndSelected);
    const b2cPay = splitpayList.find(isSplitPayMethodMMT);

    // if no reward is selected, show the first reward from the list
    if (isEmpty(rewardPay)) {
        rewardPay = getFirstRewardFromList(splitpayList);
    }

    const firstFoldSplitPayList = [b2cPay, smPay, rewardPay].filter(
        (method): method is SplitPayData => method !== undefined,
    );
    const secondFold = splitpayList.filter((m) => !firstFoldSplitPayList.includes(m));

    return { firstFoldSplitPayList, canShowViewAllRewards: !isEmpty(secondFold) };
};

export const getSplitpayClickEventContext = (data: SplitPayData, selected: boolean) => {
    if (data.isReward) {
        return JSON.stringify({
            methodName: `Rewards: ${data.splitPayIdentifier}`,
            selected,
        });
    } else if (isSplitPayMethodSwiggyPay(data) || isSplitPayMethodMMT(data)) {
        return JSON.stringify({
            methodName: data.paymentCode,
            selected,
        });
    }
    return "";
};
